import React, { useEffect, useState, useContext } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom"; // 🎯 ADDED useNavigate
import axios from "axios";
import { CartContext } from "../../context/CartContext";
import "./Shop.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const PRODUCT_API = `${API_BASE_URL}/api/products`;
const CATEGORY_API = `${API_BASE_URL}/api/categories`;
const PRODUCTS_PER_PAGE = 12;
const FALLBACK_IMAGE = "https://via.placeholder.com/400x400?text=No+Image";

const Shop = () => {
  const { addToCart } = useContext(CartContext);
  const navigate = useNavigate(); // 🎯 Initialize navigate
  const [searchParams, setSearchParams] = useSearchParams();

  // 1. READ BOTH CATEGORY AND SEARCH FROM URL QUERY PARAMS
  const initialCategory = searchParams.get("category") || "All";
  const initialSearch = searchParams.get("search") || "";

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // 2. INITIALIZE STATE FROM URL
  const [search, setSearch] = useState(initialSearch);
  const [category, setCategory] = useState(initialCategory);
  const [sortBy, setSortBy] = useState("newest");
  const [priceFilter, setPriceFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  useEffect(() => {
    fetchShopData();
  }, []);

  // 3. LISTEN TO URL CHANGES (Category AND Search)
  useEffect(() => {
    const categoryFromUrl = searchParams.get("category") || "All";
    const searchFromUrl = searchParams.get("search") || "";
    
    setCategory(categoryFromUrl);
    setSearch(searchFromUrl);
    setCurrentPage(1);
  }, [searchParams]);

  const resolveImageUrl = (img) => {
    if (!img) return FALLBACK_IMAGE;

    if (img.startsWith("http://") || img.startsWith("https://")) {
      return img;
    }

    if (img.startsWith("/uploads")) {
      return `${API_BASE_URL}${img}`;
    }

    if (img.startsWith("uploads/")) {
      return `${API_BASE_URL}/${img}`;
    }

    return `${API_BASE_URL}/uploads/${img}`;
  };

  const getProductImage = (product) => {
    if (product?.image) return resolveImageUrl(product.image);

    if (Array.isArray(product?.images) && product.images.length > 0) {
      return resolveImageUrl(product.images[0]);
    }

    return FALLBACK_IMAGE;
  };

  const fetchShopData = async () => {
    try {
      setLoading(true);

      const [productRes, categoryRes] = await Promise.all([
        axios.get(PRODUCT_API),
        axios.get(CATEGORY_API),
      ]);

      let productData = [];
      let categoryData = [];

      if (Array.isArray(productRes.data)) {
        productData = productRes.data;
      } else if (Array.isArray(productRes.data.products)) {
        productData = productRes.data.products;
      } else if (Array.isArray(productRes.data.data)) {
        productData = productRes.data.data;
      }

      if (Array.isArray(categoryRes.data)) {
        categoryData = categoryRes.data;
      } else if (Array.isArray(categoryRes.data.categories)) {
        categoryData = categoryRes.data.categories;
      } else if (Array.isArray(categoryRes.data.data)) {
        categoryData = categoryRes.data.data;
      }

      const activeProducts = productData.filter((item) => item.isActive !== false);
      const activeCategories = categoryData.filter((item) => item.isActive !== false);

      setProducts(activeProducts);
      setCategories(activeCategories);
    } catch (err) {
      console.log(err);
      setProducts([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (value) => {
    setCategory(value);
    setCurrentPage(1);
    setShowMobileFilters(false);

    const params = new URLSearchParams(searchParams);
    if (value === "All") {
      params.delete("category");
    } else {
      params.set("category", value);
    }
    setSearchParams(params);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setCurrentPage(1);

    const params = new URLSearchParams(searchParams);
    if (!value.trim()) {
      params.delete("search");
    } else {
      params.set("search", value);
    }
    setSearchParams(params);
  };

  const normalizedProducts = products.map((item) => ({
    ...item,
    categoryId:
      typeof item.category === "object"
        ? item.category?._id
        : item.category || "",
    categoryName:
      typeof item.category === "object"
        ? item.category?.name
        : item.category || "No Category",
  }));

  // 4. ROBUST MULTI-TERM SEARCH FILTERING
  let filteredProducts = normalizedProducts.filter((item) => {
    let matchesSearch = true;

    if (search.trim()) {
      const searchTerms = search
        .split(/[,|]+/)
        .map((term) => term.trim().toLowerCase())
        .filter(Boolean);

      const nameStr = (item.name || "").toLowerCase();
      const brandStr = (item.brand || "").toLowerCase();
      const skuStr = (item.sku || "").toLowerCase();
      const tagsStr = Array.isArray(item.tags)
        ? item.tags.join(" ").toLowerCase()
        : (item.tags || "").toLowerCase();

      matchesSearch = searchTerms.some(
        (term) =>
          nameStr.includes(term) ||
          brandStr.includes(term) ||
          skuStr.includes(term) ||
          tagsStr.includes(term)
      );
    }

    const matchesCategory =
      category === "All" || item.categoryId === category;

    let matchesPrice = true;
    switch (priceFilter) {
      case "0-500":
        matchesPrice = item.price <= 500;
        break;
      case "500-1000":
        matchesPrice = item.price > 500 && item.price <= 1000;
        break;
      case "1000-5000":
        matchesPrice = item.price > 1000 && item.price <= 5000;
        break;
      case "5000+":
        matchesPrice = item.price > 5000;
        break;
      default:
        matchesPrice = true;
    }

    const matchesStock =
      stockFilter === "all"
        ? true
        : stockFilter === "instock"
        ? item.stock > 0
        : item.stock <= 0;

    return (
      matchesSearch &&
      matchesCategory &&
      matchesPrice &&
      matchesStock
    );
  });

  filteredProducts.sort((a, b) => {
    switch (sortBy) {
      case "priceLow":
        return a.price - b.price;
      case "priceHigh":
        return b.price - a.price;
      case "newest":
        return new Date(b.createdAt) - new Date(a.createdAt);
      default:
        return 0;
    }
  });

  const totalPages = Math.ceil(
    filteredProducts.length / PRODUCTS_PER_PAGE
  );

  const indexOfLast = currentPage * PRODUCTS_PER_PAGE;
  const indexOfFirst = indexOfLast - PRODUCTS_PER_PAGE;

  const currentProducts = filteredProducts.slice(
    indexOfFirst,
    indexOfLast
  );

  const clearFilters = () => {
    setSearch("");
    setCategory("All");
    setSortBy("newest");
    setPriceFilter("all");
    setStockFilter("all");
    setCurrentPage(1);
    setSearchParams({});
    setShowMobileFilters(false);
  };

  return (
    <div className="shop-page">
      <div className="shop-header desktop-only">
        <div className="container">
          <h1>Shop Products</h1>
          <p>Explore Premium Products</p>
        </div>
      </div>

      <div className="mobile-filter-bar">
        <button 
          className="mobile-filter-toggle-btn"
          onClick={() => setShowMobileFilters(!showMobileFilters)}
        >
          <i className="fa-solid fa-filter"></i> 
          {showMobileFilters ? "Hide Filters" : "Filter & Sort"}
        </button>
      </div>

      <div className="shop-layout">
        <aside className={`filter-sidebar ${showMobileFilters ? "show" : ""}`}>
          <div className="filter-sidebar-header">
            <h3>Filters & Sort</h3>
            <button 
              className="close-filter-btn" 
              onClick={() => setShowMobileFilters(false)}
            >
              &times;
            </button>
          </div>

          <div className="filter-group">
            <label>Search Keyword</label>
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #ccc",
                boxSizing: "border-box"
              }}
            />
          </div>

          <div className="filter-group">
            <label>Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setShowMobileFilters(false);
              }}
            >
              <option value="newest">Newest First</option>
              <option value="priceLow">Price Low → High</option>
              <option value="priceHigh">Price High → Low</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Category</label>
            <select
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              <option value="All">All Categories</option>
              {categories.map((cat) => (
                <option
                  key={cat._id || cat.id}
                  value={cat._id || cat.id}
                >
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Price Range</label>
            <select
              value={priceFilter}
              onChange={(e) => {
                setPriceFilter(e.target.value);
                setCurrentPage(1);
                setShowMobileFilters(false);
              }}
            >
              <option value="all">All Prices</option>
              <option value="0-500">₹0-₹500</option>
              <option value="500-1000">₹500-₹1000</option>
              <option value="1000-5000">₹1000-₹5000</option>
              <option value="5000+">₹5000+</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Stock Status</label>
            <select
              value={stockFilter}
              onChange={(e) => {
                setStockFilter(e.target.value);
                setCurrentPage(1);
                setShowMobileFilters(false);
              }}
            >
              <option value="all">All Stock Status</option>
              <option value="instock">In Stock</option>
              <option value="outstock">Out Of Stock</option>
            </select>
          </div>

          <button
            className="clear-btn"
            onClick={clearFilters}
          >
            Clear Filters
          </button>
        </aside>

        <div className="shop-content">
          <div className="shop-toolbar">
            <h4>Showing {filteredProducts.length} Products {search && `for "${search}"`}</h4>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Newest First</option>
              <option value="priceLow">Price Low → High</option>
              <option value="priceHigh">Price High → Low</option>
            </select>
          </div>

          {loading ? (
            <div className="loading-box">
              <h2>Loading Products...</h2>
            </div>
          ) : currentProducts.length === 0 ? (
            <div className="loading-box">
              <h2>No products found matching your search.</h2>
            </div>
          ) : (
            <div className="products-grid">
              {currentProducts.map((product) => {
                // 🎯 CUSTOMIZATION LOGIC ADDED HERE
                const isCustomizable = 
                  (product?.sku && product.sku.toUpperCase().startsWith("CUSTOM")) || 
                  (Array.isArray(product?.tags) && product.tags.includes("personalized"));
                
                const targetUrl = isCustomizable 
                  ? `/customize-cushion/${product._id}` 
                  : `/product/${product._id}`;

                return (
                  <div className="product-card" key={product._id}>
                    <Link
                      to={targetUrl}
                      className="product-link"
                    >
                      {/* 🎯 RELATIVE WRAPPER FOR BADGE */}
                      <div style={{ position: 'relative' }}>
                        <img
                          src={getProductImage(product)}
                          alt={product.name}
                        />
                        
                        {/* 🎯 FLOATING BADGE */}
                        {isCustomizable && (
                          <span style={{ 
                            position: 'absolute', 
                            top: '10px', 
                            right: '10px', 
                            background: '#9c6b3d', 
                            color: '#fff', 
                            padding: '4px 10px', 
                            borderRadius: '20px', 
                            fontSize: '11px',
                            fontWeight: 'bold',
                            letterSpacing: '0.5px'
                          }}>
                            Personalize Me!
                          </span>
                        )}
                      </div>

                      <h3 style={{ margin: 0, padding: "10px" }}>{product.name}</h3>

                      <div
                        style={{
                          padding: "10px",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          flexWrap: "wrap",
                        }}
                      >
                        <p style={{ margin: 0 }}>
                          {product.originalPrice ? (
                            <>
                              <i
                                className="fa-solid fa-arrow-down"
                                style={{ fontSize: "18px", marginRight: "4px" }}
                              ></i>
                              {Math.round((product.price / product.originalPrice) * 100)}%
                            </>
                          ) : (
                            ""
                          )}
                        </p>

                        <p
                          style={{
                            margin: 0,
                            textDecoration: "line-through",
                            color: "#888",
                          }}
                        >
                          {product.originalPrice ? `₹${product.originalPrice}` : ""}
                        </p>

                        <p style={{ margin: 0, fontWeight: "bold", color: "#000" }}>
                          ₹{product.price}
                        </p>
                      </div>
                    </Link>

                    {/* 🎯 BUTTON ROUTING LOGIC */}
                    <button
                      onClick={() => {
                        if (isCustomizable) {
                          navigate(targetUrl); // Send to customizer
                        } else {
                          addToCart({ ...product, quantity: 1 }); // Standard add to cart
                        }
                      }}
                    >
                      {isCustomizable ? "Customize Now" : "Add To Cart"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {totalPages > 1 && (
            <div className="pagination">
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  className={
                    currentPage === index + 1 ? "active" : ""
                  }
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

Shop.displayName = "Shop";

export default Shop;